import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import {
  CheckoutElementsProvider,
  PaymentElement,
  useCheckoutElements,
} from '@stripe/react-stripe-js/checkout';
import callApi from 'client/util/apiCaller';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

const messages = defineMessages({
  title: {
    id: 'Billing.PaymentDialog.Title',
    defaultMessage: 'Update payment method',
  },
  save: {
    id: 'Billing.PaymentDialog.Save',
    defaultMessage: 'Save payment method',
  },
  cancel: {
    id: 'Billing.PaymentDialog.Cancel',
    defaultMessage: 'Cancel',
  },
  error: {
    id: 'Billing.PaymentDialog.Error',
    defaultMessage: 'Could not update payment method. Please try again.',
  },
  loading: {
    id: 'Billing.PaymentDialog.Loading',
    defaultMessage: 'Loading payment form…',
  },
});

function PaymentForm({ onClose, onUpdated, sessionId }) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const checkoutState = useCheckoutElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async event => {
    event.preventDefault();
    if (checkoutState.type !== 'success') {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const confirmResult = await checkoutState.checkout.confirm();
      if (confirmResult.type === 'error') {
        setError(confirmResult.error.message || formatMessage(messages.error));
        return;
      }

      const ret = await callApi(
        dispatch,
        'users/subscriptions/payment-method/confirm-checkout',
        null,
        'POST',
        { sessionId },
      );
      onUpdated(ret);
      onClose();
    } catch (_err) {
      setError(formatMessage(messages.error));
    } finally {
      setLoading(false);
    }
  };

  if (checkoutState.type === 'loading') {
    return (
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {formatMessage(messages.loading)}
        </Typography>
      </DialogContent>
    );
  }

  if (checkoutState.type === 'error') {
    return (
      <DialogContent>
        <Typography variant="body2" color="error">
          {checkoutState.error.message || formatMessage(messages.error)}
        </Typography>
      </DialogContent>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <DialogContent>
        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        <PaymentElement
          options={{
            layout: {
              type: 'tabs',
              defaultCollapsed: false,
            },
            wallets: {
              link: 'never',
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {formatMessage(messages.cancel)}
        </Button>
        <Button type="submit" variant="contained" disabled={loading}>
          {formatMessage(messages.save)}
        </Button>
      </DialogActions>
    </form>
  );
}

PaymentForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  onUpdated: PropTypes.func.isRequired,
  sessionId: PropTypes.string.isRequired,
};

function UpdatePaymentMethodDialog({ open, onClose, onUpdated }) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const [clientSecret, setClientSecret] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [publishableKey, setPublishableKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setSessionId(null);
      setPublishableKey(null);
      setLoadError(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(false);

    callApi(dispatch, 'users/subscriptions/payment-method/checkout-session', null, 'POST')
      .then(res => {
        if (cancelled) return;
        setClientSecret(res.clientSecret);
        setSessionId(res.sessionId);
        setPublishableKey(res.publishableKey);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, dispatch]);

  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{formatMessage(messages.title)}</DialogTitle>
      {loading && (
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {formatMessage(messages.loading)}
          </Typography>
        </DialogContent>
      )}
      {loadError && (
        <DialogContent>
          <Typography variant="body2" color="error">
            {formatMessage(messages.error)}
          </Typography>
        </DialogContent>
      )}
      {!loading && !loadError && clientSecret && stripePromise && sessionId && (
        <CheckoutElementsProvider stripe={stripePromise} options={{ clientSecret }}>
          <PaymentForm onClose={onClose} onUpdated={onUpdated} sessionId={sessionId} />
        </CheckoutElementsProvider>
      )}
    </Dialog>
  );
}

UpdatePaymentMethodDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdated: PropTypes.func.isRequired,
};

export default UpdatePaymentMethodDialog;
